import {
  all,
  call,
  put,
  select,
  takeLatest,
  takeEvery,
} from 'redux-saga/effects'
import { startAsyncValidation, stopAsyncValidation } from 'redux-form/immutable'
import { toast } from 'react-toastify'

import api from 'api'

import { NAME as CREATE_ACTIVITY_ROUTE } from 'views/CreateActivity/constants'
import { NAME as ROUTE_VIEW_ACTIVITY } from 'views/ViewActivity/constants'

import { tryApiCall } from 'utilities/sagas'
import { reverse, locationSelector } from 'utilities/routes'

import { NAME } from './constants'
import actions from './actions'

function filterCriteria(criteria) {
  /**
   * Strips the criteria object from properties for which no criteria
   * were specified.
   *
   * Example:
   * >>> filterCriteria({a: [], b: [1, 2])
   * {b: [1, 2]}
   */
  return Object.keys(criteria)
    .filter(key => criteria[key] != null && criteria[key].length)
    .reduce((obj, key) => {
            obj[key] = criteria[key] // eslint-disable-line
      return obj
    }, {})
}

export function* onSearch({ payload }) {
  yield put(
    actions.set({
      loading: true,
    }),
  )
  const tags = Array.isArray(payload) ? payload : []
  const filterByType = type => tags.filter(x => x.type === type)
  const toId = x => x.id
  const toLocation = x => ({ ...x.location })
  const topicIds = filterByType('topic').map(toId)
  const schoolLocations = filterByType('school').map(toLocation)
  const schoolIds = filterByType('school').map(toId)
  const results = yield call(
    [Promise, 'all'],
    [
      api.search.findEmployees(
        filterCriteria({
          topic_ids: topicIds,
          pickup_locations: schoolLocations,
        }),
      ),
      api.search.findActivities(
        filterCriteria({
          topic_ids: topicIds,
          pickup_location_ids: schoolIds,
        }),
      ),
      api.search.findSchools(
        filterCriteria({
          topic_ids: topicIds,
          pickup_locations: schoolLocations,
          school_ids: schoolIds,
        }),
      ),
    ],
  )
  yield put(
    actions.set({
      employees: results[0].data,
      activities: results[1].data,
      schools: results[2].data,
      loading: false,
      lightboxActive: false,
    }),
  )
}

export function* onDetails({ payload }) {
  yield put(
    actions.set({
      details: null,
    }),
  )

  const method = {
    employee: [api.employee, 'get'],
    activity: [api.activity, 'get'],
  }[payload.type]

  const details = yield call(method, [payload.id])

  yield put(
    actions.set({
      details: {
        ...payload,
        ...details.data,
      },
    }),
  )
}

export function* onCreateActivity({ payload }) {
  yield put(startAsyncValidation(NAME))
  const errors = {}
  const { employee, month } = payload
  yield put(
    reverse(CREATE_ACTIVITY_ROUTE, {
      query: {
        // This is a hack to send through the entire employee object to
        // CreateActivity, while only putting the id in the query parameter
        // in the location bar. This way the employee object is available
        // straight away in CreateActivity without having to wait for a
        // network request.
        employee: {
          ...employee,
          toString: () => employee.id,
        },
        month: month.toISOString(),
      },
    }),
  )
  yield put(stopAsyncValidation(NAME, errors))
}

export function* onCreateEmptyActivity() {
  yield put(startAsyncValidation(NAME))
  yield put(
    reverse(CREATE_ACTIVITY_ROUTE, {
      query: { public: true },
    }),
  )
  const errors = {}
  yield put(stopAsyncValidation(NAME, errors))
}

export function* onJoinActivity({ payload: id }) {
  yield put(actions.set({ joining: true }))
  try {
    yield call([api.activity, 'join'], id)
  } catch ({ response }) {
    response.data.forEach(toast.error)
  }
  const url = reverse(ROUTE_VIEW_ACTIVITY, { payload: { activityId: id } })
  yield put(actions.set({ joining: false }))
  yield put(url)
}

export function* onLoadSchoolDetails({ payload: id }) {
  yield put(actions.set({ loadingMarkerDetails: id }))
  try {
    const { data } = yield call([api.school, 'get'], id)
    yield put(actions.set({ markerDetails: data }))
  } catch ({ response }) {
    yield put(actions.set({ markerDetails: null }))
  }
  yield put(actions.set({ loadingMarkerDetails: null }))
}

export function* onClearSchoolDetails() {
  yield put(actions.set({ loadingMarkerDetails: null }))
}

export function* initialize() {
  const location = yield select(locationSelector)
  if (location.query.tag == null) {
    yield* onSearch({})
  }

  try {
    const [userActivities, userChildren] = yield all([
      call(tryApiCall, 'activities', [], call([api.activity, 'list'])),
      call(tryApiCall, 'children', [], call([api.child, 'list'])),
    ])

    yield put(actions.set({ userActivities, userChildren }))
  } catch (e) {
    console.error('Failed to get activities & children', e)
  }
}

export default function*() {
  yield all([
    takeLatest(actions.search.toString(), onSearch),
    takeLatest(actions.details.toString(), onDetails),
    takeEvery(actions.createActivity.toString(), onCreateActivity),
    takeEvery(actions.createEmptyActivity.toString(), onCreateEmptyActivity),
    takeEvery(actions.joinActivity.toString(), onJoinActivity),
    takeEvery(actions.loadSchoolDetails.toString(), onLoadSchoolDetails),
    takeEvery(actions.clearSchoolDetails.toString(), onClearSchoolDetails),
  ])
}



// WEBPACK FOOTER //
// ../source/views/FindActivity/sagas.js